<script setup lang="ts">
  // Program-wide movements feed (audit view, ClubManage-only): every points
  // and balance entry across all members, with kind/source/date filters.
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import {
    ClubService,
    TClubActivityKind,
    TClubActivitySource,
  } from '@/api/services/club.service';
  import V2ModalClubMember from '@/components/modals/club/V2ModalClubMember.vue';
  import PosV2HistoryDatePopover from '@/components/pos-v2/history/PosV2HistoryDatePopover.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiListbox, {
    IPosUiListboxItem,
  } from '@/components/pos-v2/ui/PosUiListbox.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import V2ClubActivityRow from '@/components/v2/club/V2ClubActivityRow.vue';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import {
    DEFAULT_HISTORY_DATE_PRESET,
    getPresetRange,
  } from '@/domain/pos/history-date-presets';
  import type { IHistoryDateRange } from '@/domain/pos/history-date-presets';
  import { IClubActivityItem } from '@/interfaces/IClubActivity';
  import { IClubMember } from '@/interfaces/IClubMember';
  import { IClubProgram } from '@/interfaces/IClubProgram';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  const props = defineProps<{
    program: IClubProgram;
  }>();

  const modals = useModal();
  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  //#region Filters

  const KIND_IDS: ReadonlyArray<TClubActivityKind | 'all'> = [
    'all',
    'adjust',
    'topup',
    'redeem',
    'reversal',
  ];
  const SOURCE_IDS: ReadonlyArray<TClubActivitySource | 'all'> = [
    'all',
    'points',
    'cash',
  ];

  const kind = ref<TClubActivityKind | 'all'>('all');
  const source = ref<TClubActivitySource | 'all'>('all');
  const range = ref<IHistoryDateRange>(
    getPresetRange(DEFAULT_HISTORY_DATE_PRESET)
  );

  const kindItems = computed<IPosUiListboxItem[]>(() => [
    { id: 'all', label: i18nT('club.movements.kindAll') },
    { id: 'adjust', label: i18nT('club.movements.kindAdjust') },
    { id: 'topup', label: i18nT('club.movements.kindTopup') },
    { id: 'redeem', label: i18nT('club.movements.kindRedeem') },
    { id: 'reversal', label: i18nT('club.movements.kindReversal') },
  ]);

  const sourceItems = computed<IPosUiListboxItem[]>(() => [
    { id: 'all', label: i18nT('club.movements.sourceAll') },
    { id: 'points', label: i18nT('club.movements.sourcePoints') },
    { id: 'cash', label: i18nT('club.movements.sourceCash') },
  ]);

  function onKindInput(value: number | string | null) {
    kind.value = KIND_IDS.find((id) => id === value) ?? 'all';
  }

  function onSourceInput(value: number | string | null) {
    source.value = SOURCE_IDS.find((id) => id === value) ?? 'all';
  }

  function onRangeApply(value: IHistoryDateRange) {
    range.value = value;
  }

  //#endregion

  //#region Feed (keyset cursor; any filter change resets it)

  const items = ref<IClubActivityItem[]>([]);
  const nextCursor = ref<string | null>(null);
  const loaded = ref(false);
  const loading = ref(false);

  // Guards against a stale response landing after the filters changed.
  let fetchSeq = 0;

  async function loadMore() {
    const restaurantId = restaurantStore.restaurant?.id;
    if (!restaurantId || loading.value) return;
    if (loaded.value && !nextCursor.value) return;

    const seq = ++fetchSeq;
    loading.value = true;
    try {
      const page = await ClubService.getActivity(restaurantId, {
        cursor: loaded.value ? nextCursor.value : null,
        kind: kind.value === 'all' ? null : kind.value,
        source: source.value === 'all' ? null : source.value,
        dateFrom: range.value.from,
        // Exclusive bound; null = "now" (server-side, no upper bound).
        dateTo: range.value.to,
      });
      if (seq !== fetchSeq) return;
      items.value.push(...page.items);
      nextCursor.value = page.nextCursor;
      loaded.value = true;
    } catch (e) {
      if (seq !== fetchSeq) return;
      // Mark as loaded so a failed first fetch shows the empty state
      // (with the error toast) instead of permanent skeletons.
      loaded.value = true;
      ui.serverError(e, i18nT('club.movements.loadError'));
    } finally {
      if (seq === fetchSeq) loading.value = false;
    }
  }

  async function reload() {
    fetchSeq++; // Invalidate any in-flight request.
    items.value = [];
    nextCursor.value = null;
    loaded.value = false;
    loading.value = false;
    await loadMore();
  }

  watch(() => restaurantStore.restaurant?.id, reload, { immediate: true });
  watch([kind, source, range], () => reload());

  //#endregion

  //#region Member drill-down

  function openMember(item: IClubActivityItem) {
    if (!item.memberId) return;

    modals.show(
      V2ModalClubMember,
      { memberId: item.memberId, program: props.program },
      {
        name: 'v2-club-member',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        width: 680,
        maxWidth: Math.round(window.innerWidth * 0.95),
      },
      {
        // Any action inside the modal (adjust/topup/block) writes ledger
        // entries, so refresh the feed from page 1.
        updated: (_updated: IClubMember) => {
          reload();
        },
      }
    );
  }

  //#endregion
</script>

<template>
  <div class="v2-club-movements">
    <div class="movements-toolbar">
      <PosUiListbox
        :value="kind"
        :items="kindItems"
        class="filter-kind"
        @input="onKindInput"
      />
      <PosUiListbox
        :value="source"
        :items="sourceItems"
        class="filter-source"
        @input="onSourceInput"
      />
      <PosV2HistoryDatePopover :range="range" @apply="onRangeApply" />
    </div>

    <template v-if="!loaded">
      <div class="movements-skeletons">
        <PosUiSkeleton v-for="n in 6" :key="n" class="skeleton-row" />
      </div>
    </template>

    <PosUiEmptyState
      v-else-if="items.length === 0"
      icon="swap-horizontal"
      :title="$t('club.movements.empty')"
    />

    <div v-else class="movements-list">
      <button
        v-for="item in items"
        :key="`${item.source}-${item.id}`"
        class="movement-row"
        type="button"
        @click="openMember(item)"
      >
        <V2ClubActivityRow :item="item" show-member />
      </button>

      <PosUiButton
        v-if="nextCursor"
        color="neutral"
        variant="ghost"
        size="sm"
        block
        :loading="loading"
        @click="loadMore"
      >
        {{ $t('club.member.activityMore') }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-club-movements {
    @apply tw-flex tw-flex-col tw-gap-3;

    .movements-toolbar {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;

      .filter-kind {
        @apply tw-w-full sm:tw-w-56;
      }

      .filter-source {
        @apply tw-w-full sm:tw-w-44;
      }
    }

    .movements-skeletons {
      @apply tw-flex tw-flex-col tw-gap-2;

      .skeleton-row {
        @apply tw-h-14 tw-rounded-xl;
      }
    }

    .movements-list {
      @apply tw-flex tw-flex-col tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white tw-px-4 tw-py-1;

      .movement-row {
        @apply tw-w-full tw-border-b tw-border-neutral-100 tw-text-left tw-transition-colors last-of-type:tw-border-b-0 hover:tw-bg-neutral-50;

        // The row is the only child of its wrapper button, so its own
        // last:border-b-0 always fires; the separator lives on the wrapper.
        :deep(.v2-club-activity-row) {
          @apply tw-border-b-0;
        }
      }
    }
  }
</style>
